import { useEffect, useState } from 'react';
import { useMatch, useSearchParams } from 'react-router';
import { newAccountTrialDurationDays } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { withSignupAttributionParams } from '@gonfalon/signup/agentUtmParams';
import { handleSignupIntentTabChange, SIGNUP_TAB_ENTERPRISE_DEMO, SIGNUP_TAB_TRIAL } from '@gonfalon/signup/analytics';
import { captureSignupOAuthReturnPath } from '@gonfalon/signup/oauthReturnPathStorage';
import { NewTabLink } from '@gonfalon/ui/NewTabLink';

import { clearAgentControlSignupPostAuthDestination } from '#utils/signupPostAuthDestinationStorage';

import { FlagControlledSignUpBannerContainer } from './FlagControlledSignUpBannerContainer';
import { OAuthButtons } from './OAuthButtons';
import { SignUpForm } from './SignUpForm';
import { SignUpIntentTabs } from './SignUpIntentTabs';
import { useCaptureSignupLandingUrl } from './useCaptureSignupLandingUrl';

import styles from './SignUpMain.module.css';

type SignUpMainProps = {
  headingClassName?: string;
  /** When false (default), clears Agent Control post-signup redirect if user switches to a generic signup flow. */
  agentControl?: boolean;
  /** Google OAuth pre-auth navigates here so session state (UTMs, post-signup dest.) stays in sync. */
  oauthRedirectPath?: string;
  /** Trial length quoted in the signup copy. Entry points with a longer trial pass their own value. */
  trialDurationDays?: number;
  showSignInLink?: boolean;
  awsMarketplace?: boolean;
};

export const SignUpMain = ({
  headingClassName,
  agentControl = false,
  oauthRedirectPath = '/signup',
  trialDurationDays = newAccountTrialDurationDays(),
  showSignInLink = true,
  awsMarketplace = false,
}: SignUpMainProps) => {
  const isSignupPage = useMatch({ path: '/signup', end: true });
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  useCaptureSignupLandingUrl();

  useEffect(() => {
    captureSignupOAuthReturnPath(redirect);
  }, [redirect]);

  useEffect(() => {
    if (!agentControl) {
      clearAgentControlSignupPostAuthDestination();
    }
  }, [agentControl]);

  const showEnterpriseDemoTabs = !agentControl;
  const [selectedIntentTab, setSelectedIntentTab] = useState(SIGNUP_TAB_TRIAL);
  const isEnterpriseDemoIntent = selectedIntentTab === SIGNUP_TAB_ENTERPRISE_DEMO;

  const handleIntentTabChange = (nextTab: string) => {
    handleSignupIntentTabChange(selectedIntentTab, nextTab);
    setSelectedIntentTab(nextTab);
  };

  return (
    <>
      <hgroup className={headingClassName}>
        {showEnterpriseDemoTabs && isEnterpriseDemoIntent ? (
          <>
            <h1>
              <Message id="signUp.signUpPage.enterpriseHeading" />
            </h1>
            <p>
              <Message id="signUp.signUpPage.enterpriseBody" />
            </p>
          </>
        ) : (
          <>
            <h1>
              <Message id="signUp.signUpPage.heading" />
            </h1>
            {awsMarketplace ? (
              <p>
                <span>
                  <Message id="signUp.signUpPage.awsTrialPrefix" values={{ days: trialDurationDays }} />{' '}
                </span>
                <NewTabLink
                  className={styles.link}
                  href="https://launchdarkly.com/pricing/"
                  text={msg('signUp.signUpPage.foundationPlanLink')}
                  showIcon={false}
                />
                <span>
                  {' '}
                  <Message id="signUp.signUpPage.awsTrialSuffix" />
                </span>
              </p>
            ) : (
              <p>
                <span>
                  <Message id="signUp.signUpPage.trialPrefix" values={{ days: trialDurationDays }} />{' '}
                </span>
                <NewTabLink
                  className={styles.link}
                  href="https://launchdarkly.com/pricing/"
                  text={msg('signUp.signUpPage.developerAccountLink')}
                  showIcon={false}
                />
                <span>
                  {' '}
                  <Message id="signUp.signUpPage.freeTierSuffix" />
                </span>
              </p>
            )}
          </>
        )}
      </hgroup>
      {isSignupPage && <FlagControlledSignUpBannerContainer />}
      {showEnterpriseDemoTabs ? (
        <SignUpIntentTabs
          selectedTab={selectedIntentTab}
          onSelectedTabChange={handleIntentTabChange}
          oauthRedirectPath={oauthRedirectPath}
          trialDurationDays={trialDurationDays}
        />
      ) : (
        <>
          <OAuthButtons oauthRedirectPath={oauthRedirectPath} />
          <section>
            <SignUpForm trialDurationDays={trialDurationDays} />
          </section>
        </>
      )}
      <div className={styles.signInDisclaimer}>
        {showSignInLink && (
          <small>
            <span>
              <Message id="signUp.signUpPage.alreadyHaveAn" />{' '}
            </span>
            <a href={withSignupAttributionParams('/login')}>
              <Message id="signUp.signUpPage.signIn" />
            </a>
          </small>
        )}
        <small>
          <span>
            <Message id="signUp.signUpPage.legalPrefix" />{' '}
          </span>
          <NewTabLink
            href="https://launchdarkly.com/policies/subscription-terms/"
            text={msg('signUp.signUpPage.termsLink')}
            showIcon={false}
          />
          <span> and </span>
          <NewTabLink
            href="https://launchdarkly.com/policies/privacy/"
            text={msg('signUp.signUpPage.privacyLink')}
            showIcon={false}
          />
          <span>.</span>
        </small>
      </div>
    </>
  );
};
